/**
 * Phase 3 — Consolidated Approvals Tracker (Iter 200).
 * Read-only rollup of Drawing approvals + RFIs + NCRs per project, surfacing
 * what's slow across the three workflows. Click-through to existing modules.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilRuler, MessageSquareWarning, ShieldAlert, ArrowUpRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const Stat = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate: 'text-slate-700', red: 'text-red-600', amber: 'text-amber-600',
    emerald: 'text-emerald-700', blue: 'text-blue-700',
  };
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`font-display font-bold tabular-nums ${tones[tone]}`}>{value}</span>
    </div>
  );
};

const TrackerCard = ({ icon: Icon, title, headline, headlineTone, children, onOpen, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={testid}>
    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-sm bg-slate-100 flex items-center justify-center text-slate-600"><Icon size={15} /></div>
        <div>
          <div className="kpi-overline">{title}</div>
          <div className={`font-display font-extrabold text-2xl tabular-nums ${headlineTone}`}>{headline}</div>
        </div>
      </div>
      <button onClick={onOpen} className="text-[11px] font-display font-bold uppercase tracking-widest text-[#3b4872] hover:underline flex items-center gap-1" data-testid={`${testid}-open`}>
        Open <ArrowUpRight size={11} />
      </button>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export default function ApprovalsTracker() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { apiGet('/projects').then(setProjects).catch(() => toast.error('Failed to load projects')); }, []);
  useEffect(() => { if (!projectId && projects.length) setProjectId(projects[0].id); }, [projects, projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    apiGet(`/project-execution/approvals-tracker?project_id=${projectId}`)
      .then(setData).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [projectId]);

  const dw = data?.drawings || {};
  const rfi = data?.rfis || {};
  const ncr = data?.ncrs || {};

  return (
    <div>
      <PageHeader overline="Project Execution" title="Approvals Tracker" subtitle="Drawings · RFIs · NCRs — what's slow across the three workflows" />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">Project</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} data-testid="approvals-project-select" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white min-w-[280px]">
            {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-xs text-slate-400 uppercase tracking-widest py-16 font-display">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <TrackerCard icon={PencilRuler} title="Drawing Approvals · Pending" headline={dw.pending || 0} headlineTone={dw.pending ? 'text-amber-600' : 'text-emerald-700'} onOpen={() => navigate('/app/engineering')} testid="tracker-drawings">
            <Stat label="With Internal QA" value={dw.with_internal_qa || 0} tone="slate" />
            <Stat label="With Consultant" value={dw.with_consultant || 0} tone="amber" />
            <Stat label="With Client" value={dw.with_client || 0} tone="blue" />
            <Stat label="Approved" value={dw.approved || 0} tone="emerald" />
            <Stat label="Rejected" value={dw.rejected || 0} tone="red" />
            <Stat label="Total drawings" value={dw.total || 0} />
          </TrackerCard>

          <TrackerCard icon={MessageSquareWarning} title="RFIs · Pending" headline={rfi.pending || 0} headlineTone={rfi.pending ? 'text-amber-600' : 'text-emerald-700'} onOpen={() => navigate('/app/engineering')} testid="tracker-rfis">
            <Stat label="Raised (total)" value={rfi.raised || 0} />
            <Stat label="Open" value={rfi.open || 0} tone="red" />
            <Stat label="Sent" value={rfi.sent || 0} tone="amber" />
            <Stat label="Responded" value={rfi.responded || 0} tone="blue" />
            <Stat label="Closed" value={rfi.closed || 0} tone="emerald" />
          </TrackerCard>

          <TrackerCard icon={ShieldAlert} title="NCRs · Open" headline={ncr.open || 0} headlineTone={ncr.open ? 'text-red-600' : 'text-emerald-700'} onOpen={() => navigate('/app/quality')} testid="tracker-ncrs">
            <Stat label="Open (no CAPA)" value={ncr.open || 0} tone="red" />
            <Stat label="In CAPA" value={ncr.in_capa || 0} tone="amber" />
            <Stat label="Closed" value={ncr.closed || 0} tone="emerald" />
            <Stat label="Total NCRs" value={ncr.total || 0} />
          </TrackerCard>
        </div>
      )}
    </div>
  );
}
